import {
  LegacyCard,
  Pagination,
  LegacyStack,
  Badge,
  Divider,
  IndexTable,
  Link,
  Text,
  Tabs,
  VerticalStack,
  HorizontalStack,
  Button,
  Icon,
} from '@shopify/polaris';
import { ChevronDownMinor, ChevronRightMinor } from '@shopify/polaris-icons';
import { useEffect, useState, useContext, forwardRef, useImperativeHandle } from 'react';
import { getDeposits, depositEdit, deleteDeposit, listGroups } from '../../../services/depositsService';
import { SET_ERROR, SET_OK } from '../../../context/ToastReducer';
import ToastContext from '../../../context/ToastContext';
import { useHistory } from 'react-router';
import EditDepositModal from './EditDepositModal';
import { depositTypeBadgeStatus, depositTypeLabel } from '../../../helpers/depositTypes';

const tabsMenu = [
  { id: 'deposits', content: 'Depósitos' },
  { id: 'groups', content: 'Grupos' },
];

const headingsDeposits = [
  { title: 'Depósito' },
  { title: '' },
  { title: 'Rol' },
  { title: 'Grupo' },
  { title: 'Uds.', alignment: 'end' },
  { title: 'Serie-Color', alignment: 'end' },
  { title: 'Stock', alignment: 'end' },
];

const headingsGroups = [{ title: 'Grupo' }, { title: '' }];

const fmt = (n) => (n != null ? Intl.NumberFormat('es-ES').format(n) : '—');

const ListDeposits = forwardRef(({ page, multiMode = false }, ref) => {
  const [, dispatchToast] = useContext(ToastContext);
  const [response, setResponse] = useState(null);
  const [rows, setRows] = useState([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [allLeafDeposits, setAllLeafDeposits] = useState([]);
  const [allGroups, setAllGroups] = useState([]);
  const [editGroupId, setEditGroupId] = useState(null);
  const [selectedDeposits, setSelectedDeposits] = useState([]);
  const history = useHistory();

  useImperativeHandle(ref, () => ({
    last(e = { new: false }) {
      callDB(e.new ? Math.ceil((response.metadata.count + 1) / response.metadata.limit) : response.metadata.lastPage);
    },
  }));

  // En modo multidepósito se cargan todos los depósitos operables de una vez y
  // se oculta el paginador: así la selección no se pierde al cambiar de página.
  const callDB = (pg = 1, limit = 50, tab) => {
    const currentTab = tab !== undefined ? tab : selectedTab;
    if (!multiMode) history.push(`/deposits/page/${pg}`);
    if (currentTab === 0) {
      getDeposits({ page: multiMode ? 1 : pg, limit: multiMode ? 1000 : limit, excludeGroups: true })
        .then((res) => setResponse(res.data))
        .catch((e) => dispatchToast({ type: SET_ERROR, payload: e.response.data }));
    } else {
      Promise.all([
        getDeposits({ onlyGroups: true, page: pg, limit }),
        getDeposits({ excludeGroups: true, limit: 1000 }),
      ])
        .then(([groupsRes, leafRes]) => {
          setResponse(groupsRes.data);
          setAllLeafDeposits(leafRes.data.data || []);
        })
        .catch((e) => dispatchToast({ type: SET_ERROR, payload: e.response?.data }));
    }
  };

  useEffect(() => {
    callDB(page);
    listGroups().then((r) => setAllGroups(r.data.data || []));
    // eslint-disable-next-line
  }, []);

  // Al entrar o salir del modo multidepósito volvemos a la pestaña de depósitos
  // operables (los grupos no tienen stock propio) y recargamos el listado.
  useEffect(() => {
    setSelectedDeposits([]);
    setSelectedTab(0);
    setExpandedGroups(new Set());
    callDB(1, 50, 0);
    // eslint-disable-next-line
  }, [multiMode]);

  const selectable = multiMode && selectedTab === 0;

  const toggleDeposit = (id) =>
    setSelectedDeposits((prev) => (prev.includes(id) ? prev.filter((d) => d !== id) : [...prev, id]));

  const handleSelectionChange = (selectionType, isSelecting, selection) => {
    const pageIds = (response?.data || []).map((d) => String(d.id));

    if (selectionType === 'page' || selectionType === 'all')
      return setSelectedDeposits(isSelecting ? pageIds : []);

    if (selectionType === 'multi' && Array.isArray(selection)) {
      // `selection` llega como [desde, hasta] (índices de fila) en shift-click.
      const [from, to] = selection;
      const range = pageIds.slice(Math.min(from, to), Math.max(from, to) + 1);
      return setSelectedDeposits((prev) =>
        isSelecting ? [...new Set([...prev, ...range])] : prev.filter((d) => !range.includes(d)),
      );
    }

    return toggleDeposit(String(selection));
  };

  const applyMultiSelection = () =>
    history.push(`/deposits/multi?deposits=${selectedDeposits.join(':')}`);

  const toggleExpand = (id) =>
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const handleEditGroup = (values) =>
    depositEdit(editGroupId, values)
      .then(() => {
        dispatchToast({ type: SET_OK, payload: { message: 'Grupo actualizado' } });
        setEditGroupId(null);
        callDB(response?.metadata?.page || 1, response?.metadata?.limit || 50, 1);
        listGroups().then((r) => setAllGroups(r.data.data || []));
      })
      .catch((e) => dispatchToast({ type: SET_ERROR, payload: e.response.data }));

  const handleDeleteGroup = () =>
    deleteDeposit(editGroupId)
      .then(() => {
        dispatchToast({ type: SET_OK, payload: { message: 'Grupo eliminado' } });
        setEditGroupId(null);
        callDB(1, 50, 1);
        listGroups().then((r) => setAllGroups(r.data.data || []));
      })
      .catch((e) => dispatchToast({ type: SET_ERROR, payload: e.response.data }));

  const rowDeposit = (
    { id, name, code, main, parentId, parentGroup, depositType, distinctVariants, distinctProducts, stockQty },
    index,
  ) => {
    const groupName = parentGroup?.name || allGroups.find((g) => g.id === parentId)?.name;
    return (
    <IndexTable.Row
      id={String(id)}
      key={id}
      position={index}
      selected={selectable ? selectedDeposits.includes(String(id)) : undefined}
    >
      <IndexTable.Cell>
        {/* En modo multidepósito el click de la fila marca en vez de navegar. */}
        {!selectable && <Link dataPrimaryLink url={`/deposits/${id}/`} />}
        <VerticalStack gap="0">
          <Text variant="bodyMd" fontWeight="bold" as="span">{name}</Text>
          <Text variant="bodySm" color="subdued" as="span">{code}</Text>
        </VerticalStack>
      </IndexTable.Cell>
      <IndexTable.Cell>{main && <Badge status="info">Principal</Badge>}</IndexTable.Cell>
      <IndexTable.Cell>
        <Badge status={depositTypeBadgeStatus(depositType)}>{depositTypeLabel(depositType)}</Badge>
      </IndexTable.Cell>
      <IndexTable.Cell>
        {groupName && <Text variant="bodySm" color="subdued">{groupName}</Text>}
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text alignment="end">{fmt(distinctProducts)}</Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text alignment="end">{fmt(distinctVariants)}</Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text alignment="end" fontWeight="semibold">{fmt(stockQty)}</Text>
      </IndexTable.Cell>
    </IndexTable.Row>
  );
  };

  const rowGroup = ({ id, name, code }, index, isExpanded) => (
    <IndexTable.Row id={id} key={id} position={index}>
      <IndexTable.Cell>
        <HorizontalStack gap="2" blockAlign="center">
          <button
            onClick={() => toggleExpand(id)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', display: 'flex', alignItems: 'center', color: 'var(--p-color-icon)' }}
          >
            <Icon source={isExpanded ? ChevronDownMinor : ChevronRightMinor} />
          </button>
          <VerticalStack gap="0">
            <Text variant="bodyMd" fontWeight="bold" as="span">{name}</Text>
            <Text variant="bodySm" color="subdued" as="span">{code}</Text>
          </VerticalStack>
        </HorizontalStack>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button size="slim" plain onClick={() => setEditGroupId(id)}>Editar</Button>
        </div>
      </IndexTable.Cell>
    </IndexTable.Row>
  );

  const rowGroupChild = ({ id, name, code, distinctVariants, distinctProducts, stockQty }, index) => (
    <IndexTable.Row id={`child-${id}`} key={`child-${id}`} position={index} tone="subdued">
      <IndexTable.Cell>
        <div style={{ paddingLeft: '2.5rem' }}>
          <Link dataPrimaryLink url={`/deposits/${id}/`} />
          <VerticalStack gap="0">
            <Text variant="bodyMd" as="span">{name}</Text>
            <Text variant="bodySm" color="subdued" as="span">{code}</Text>
          </VerticalStack>
        </div>
      </IndexTable.Cell>
      <IndexTable.Cell />
    </IndexTable.Row>
  );

  const buildGroupRows = (groups) => {
    const flat = [];
    let pos = 0;
    groups.forEach((group) => {
      const isExpanded = expandedGroups.has(group.id);
      flat.push(rowGroup(group, pos++, isExpanded));
      if (isExpanded)
        allLeafDeposits
          .filter((d) => d.parentId === group.id)
          .forEach((child) => flat.push(rowGroupChild(child, pos++)));
    });
    return flat;
  };

  useEffect(() => {
    if (!response) return;
    setRows(
      selectedTab === 0
        ? response.data.map((r, k) => rowDeposit(r, k))
        : buildGroupRows(response.data),
    );
    // eslint-disable-next-line
  }, [response, selectedTab, expandedGroups, allLeafDeposits, selectedDeposits, selectable]);

  const handleTabChange = (tabIndex) => {
    setSelectedTab(tabIndex);
    setExpandedGroups(new Set());
    callDB(1, 50, tabIndex);
  };

  return (
    <>
      {response && (
        <>
          {!multiMode && <Tabs tabs={tabsMenu} selected={selectedTab} onSelect={handleTabChange} />}
          <IndexTable
            resourceName={{ singular: 'depósito', plural: 'depósitos' }}
            itemCount={rows.length}
            headings={selectedTab === 0 ? headingsDeposits : headingsGroups}
            selectable={selectable}
            selectedItemsCount={selectable ? selectedDeposits.length : undefined}
            onSelectionChange={selectable ? handleSelectionChange : undefined}
            promotedBulkActions={
              selectable
                ? [
                    {
                      content: `Ver stock conjunto (${selectedDeposits.length})`,
                      onAction: applyMultiSelection,
                    },
                  ]
                : undefined
            }
            hasZebraStriping
          >
            {rows}
          </IndexTable>
          {!multiMode && (
            <>
              <Divider borderStyle="divider" />
              <LegacyCard.Section>
                <LegacyStack distribution="center">
                  <Pagination
                    hasPrevious={response.metadata.prev}
                    onPrevious={() => callDB(response.metadata.page - 1, response.metadata.limit)}
                    hasNext={response.metadata.next}
                    onNext={() => callDB(response.metadata.page + 1, response.metadata.limit)}
                  />
                </LegacyStack>
              </LegacyCard.Section>
            </>
          )}
        </>
      )}
      {editGroupId && (
        <EditDepositModal
          active={!!editGroupId}
          id={editGroupId}
          onSubmit={handleEditGroup}
          onClose={() => setEditGroupId(null)}
          onDelete={handleDeleteGroup}
        />
      )}
    </>
  );
});

export default ListDeposits;
